<template lang="pug">
.uc-nav-menu.fixed.z-50(
  :class="{ 'menu--open': isOpen }"
  @keyup.esc="toggleOpen"
  ref="elRef"
  uc-word-skip=""
  :style="{ display: thumbnailType ? 'none' : undefined }"
)
  //- expose these strings for the translate script
  //- text="Course navigation and settings"
  //- text="Settings menu"
  //- text="Course navigation"
  //- text="Close"
  //- text="Badges"
  //- Text="Decrease playback speed"
  //- Text="Increase playback speed"
  //- text="Decrease font size"
  //- text="Increase font size"
  //- text="Download a course transcript document"
  uc-preload-sfx(name="menu-link")
  .nav-buttons-container.flex.md_flex-col.gap-3.md_gap-6.flex-1.m-2.fixed.top-0.z-50.scale-75.xs_scale-100.origin-top-left.rtl_origin-top-right(
    :class="[{ 'mt-12': editModeAvailable }, { 'nav-menu-badge-ml': pageBadge }]"
    :aria-label="hasTranslation('Course navigation and settings')"
    role="region"
    tabindex="-1"
  )
    uc-preload-sfx(name="click2")
    button.menu__button.bg-white.rounded-full.text-2xl.relative.focus-visible_focus-ring-8(
      :aria-expanded="isOpen"
      :class="['h-[2.75rem] w-[2.75rem] !z-[40] p-[0.125rem]', { 'shadow-md': !isOpen }]"
      :aria-label="hasTranslation(this.isOpen ? 'Close' : 'Course navigation')"
      :title="hasTranslation(this.isOpen ? 'Close' : 'Course navigation')"
      @click="toggleOpen"
      data-sfx="click2"
      v-if="!content?.course['hide-menu']"
    )
      transition-grow(:duration="0.6")
        uc-icon.absolute.inset-0.m-auto(icon="theme:menu" v-if="!isOpen")
      transition-grow(:duration="0.6" :rotate="-180")
        uc-icon.absolute.inset-0.m-auto(icon="theme:menu-close" v-if="isOpen")

      svg.menu__progress.h-10.w-10(
        :height="progress.size"
        :viewBox="`0 0 ${progress.size}  ${progress.size}`"
        :width="progress.size"
        aria-hidden="true"
      )
        circle.menu__progress-track(
          :cx="progress.center"
          :cy="progress.center"
          :r="progress.radius"
          :stroke-width="progress.stroke"
          fill="none"
          stroke="rgba(0, 0, 0, 0.05)"
        )
        circle.menu__progress-fill(
          :cx="progress.center"
          :cy="progress.center"
          :r="progress.radius"
          :stroke-dasharray="progress.circumference"
          :stroke-dashoffset="progress.offset"
          :stroke-width="progress.stroke"
          :transform="`rotate(-90 ${progress.center} ${progress.center})`"
          fill="none"
          stroke="rgb(var(--color-primary-500) / 1)"
          stroke-linecap="round"
        )

    uc-mute-button.shadow-md(v-if="!isOpen && !content.course?.audiolocked")
    uc-preload-sfx(name="settings")
    button.settings-button.bg-white.shadow-md.rounded-full.text-2xl.relative.focus-visible_focus-ring-8(
      :aria-expanded="isOpen"
      :class="'h-[2.75rem] w-[2.75rem] !z-[40] p-[0.125rem]'"
      @click="toggleOpen(); isSettings = true; settingsOpened = true"
      :aria-label="hasTranslation('Settings menu')"
      :title="hasTranslation('Settings menu')"
      data-sfx="settings"
      v-if="!isOpen"
    )
      transition-grow(:duration="0.4")
        uc-icon.absolute.inset-0.m-auto(icon="theme:settings" v-if="!isOpen")

    button.badge-button.bg-white.shadow-md.rounded-full.text-2xl.relative.focus-visible_focus-ring-8.md_hidden(
      :class="'h-[2.75rem] w-[2.75rem] !z-[40] p-[0.125rem]'"
      :aria-label="hasTranslation('Badges')"
      :title="hasTranslation('Badges')"
      @click="toggleBadges"
      v-if="!isOpen && pageBadge"
    )
      uc-asset.badge-image.p-1(
        :src="pageBadgeImage.src"
        :class="pageBadgeImage.styles"
        aria-hidden="true"
      )

  .menu__underlay.absolute.inset-0.h-screen.w-screen.auto-alpha-0.z-20(
    :class="{ 'auto-alpha-1': isOpen }"
    @click="toggleOpen"
    class="bg-black/10"
  )
  .menu__contents.fixed.inset-0.transition.bg-gray-100.dark_bg-gray-800.auto-alpha-fast.z-20(
    :class="[{ 'mt-8': editModeAvailable }, isOpen ? 'auto-alpha-1 translate-x-0 !rtl_translate-x-[0] visible' : '-translate-x-full rtl_translate-x-full invisible']"
    class="w-[480px] max-w-[90%]"
    role="navigation"
    :aria-label="hasTranslation('Course')"
  )
    uc-filter-tags(v-slot="{ filterTags }")
      .auto-alpha-0.auto-alpha-slow.flex.flex-col.shadow-2xl(
        :class="[{ 'auto-alpha-1': isOpen }, editModeAvailable ? 'h-[calc(100vh_-_32px)]' : 'h-screen']"
        v-if="isOpen"
      )
        .menu__title.flow-root.pr-6.ps-16.xs_ps-20.bg-white(
          :class="editModeAvailable ? 'py-5' : 'py-4'"
        )
          button.fancy-hover.hover_text-primary-800.prose.focus-visible_focus-ring(
            :aria-current="ariaCurrentPage(content.pages[0].id)"
            @click="toggleOpen(), goToSection(content.pages[0].id, content.pages[0].screens[0].id)"
            data-sfx="menu-link"
          )
            //- `.block` causes `<br>`s from newlines to render correctly
            uc-text.text-xl.font-bold.text-start.block(
              :text="removeLinebreaks(content.course.title)"
              k="course.title"
            )

        //- Course settings view
        form.menu__settings.prose-stack.overflow-y-auto.overflow-x-hidden.simple-scrollbar.flex-1.flex.flex-col.p-3.md_p-6.gap-10(
          v-if="isSettings"
        )
          //- AUDIO SETTINGS
          fieldset.settings-audio.flex.flex-col.md_ps-12(v-if="(hasAudio || hasSpeech || hasSfx) && !content.course?.audiolocked")
            legend.menu__settings_subtitle.font-bold.text-start.text-lg
              .flex(:class="!smallDevice && '-ms-12'")
                .w-12.flex-none(v-if="!smallDevice")
                  uc-icon.text-3xl.opacity-60(icon="theme:audio-on")
                uc-text(text="Audio settings")

            //- AUDIO
            .items-center.menu__settings_option.border-b-2.border-gray-300.text-lg(
              class="py-[16px]"
              v-if="hasAudio || hasSpeech"
            )
              label.flex.justify-end.items-center.cursor-pointer
                uc-text.labels.me-auto(text="Narration")
                input-toggle.controls(@change="toggleAudio" v-model="isAudioEnabled")

            //- SOUND EFFECTS
            .items-center.menu__settings_option.border-b-2.border-gray-300.flex.justify-end.text-lg(
              class="py-[16px]"
              v-if="hasSfx"
            )
              label.flex.justify-end.items-center.cursor-pointer.w-full
                uc-text.labels.me-auto(text="Sound effects")
                input-toggle.controls(@change="toggleSfx" v-model="isSfxEnabled")

            //- VOLUME
            .items-center.menu__settings_option.border-b-2.border-gray-300.flex.justify-end.text-lg(
              class="py-[16px]"
              v-if="hasAudio || hasSpeech || hasSfx"
            )
              label.flex.justify-end.items-center.w-full(
                :class="fontSize >= 25 && 'flex-wrap'"
              )
                uc-text.labels.me-auto(text="Volume")
                .controls.flex.items-center.justify-end
                  span.w-full
                    input.slider.focus-visible_focus-ring(
                      @input="changeVolume"
                      max="1"
                      min="0"
                      step="0.05"
                      type="range"
                      uc-word-skip=""
                      v-model="audioVolume"
                    )
                  uc-text.w-10.text-sm.ps-2(:text="`${(audioVolume * 100).toFixed(0)}%`")

            //- PLAYBACK SPEED
            .items-center.menu__settings_option.border-b-2.border-gray-300.flex.justify-end.text-lg(
              class="py-[16px]"
              v-if="hasAudio || hasSpeech"
            )
              label.flex.justify-end.items-center.w-full(
                :class="fontSize >= 25 && 'flex-wrap'"
              )
                uc-text.labels.me-auto(text="Playback speed")

                .controls.flex.items-center
                  uc-button.btn.btn-icon.ripple.ripple--centered.rounded-full.text-xl.me-2.focus-visible_focus-ring(
                    :disabled="audioPlaybackRate <= 0.25"
                    @click="changePlaybackRate(false)"
                    :aria-label="hasTranslation('Decrease playback speed')"
                  )
                    uc-text.sr-only(text="Playback speed minus")
                    uc-icon(icon="theme:minus")

                  uc-button.btn.btn-icon.ripple.ripple--centered.rounded-full.text-xl.focus-visible_focus-ring(
                    :disabled="audioPlaybackRate >= 2"
                    @click="changePlaybackRate(true)"
                    :aria-label="hasTranslation('Increase playback speed')"
                  )
                    uc-text.sr-only(text="Playback speed plus")
                    uc-icon(icon="theme:plus")

                  uc-text.text-sm.ps-2.w-10(:text="`${audioPlaybackRate}x`")

          //- VIDEO SETTINGS
          fieldset.settings-video.flex.flex-col.md_ps-12(v-if="hasVideo")
            legend.menu__settings_subtitle.font-bold.text-start.text-lg
              .flex(:class="!smallDevice && '-ms-12'")
                .w-12.flex-none(v-if="!smallDevice")
                  uc-icon.text-3xl.opacity-60(icon="theme:video-settings")
                uc-text(text="Video settings")

            //- VIDEO AUDIO
            .items-center.menu__settings_option.border-b-2.border-gray-300.flex.justify-end.text-lg(
              class="py-[16px]"
              v-if="!content.course?.audiolocked"
            )
              label.flex.justify-end.items-center.w-full.cursor-pointer
                uc-text.labels.me-auto(text="Audio")
                input-toggle.controls(@change="toggleVideoAudio" v-model="isVideoAudioEnabled")

            //- VIDEO CAPTIONS
            .items-center.menu__settings_option.video-captions.border-b-2.border-gray-300.flex.justify-end.text-lg(
              class="py-[16px]"
              v-if="hasCaptions"
            )
              label.flex.justify-end.items-center.w-full.cursor-pointer
                uc-text.labels.me-auto(text="Captions")
                input-toggle.controls(@change="toggleVideoCaptions" v-model="isCaptionsEnabled")

            //- VIDEO TRANSCRIPT
            .items-center.menu__settings_option.border-b-2.border-gray-300.flex.justify-end.text-lg(
              class="py-[16px]"
              v-if="hasCaptions"
            )
              label.flex.justify-end.items-center.w-full.cursor-pointer
                uc-text.labels.me-auto(text="Transcript")
                input-toggle.controls(@change="toggleVideoTranscript" v-model="isTranscriptEnabled")

          //- ACCESSIBILITY SETTINGS
          fieldset.settings-a11y.flex.flex-col.md_ps-12
            legend.menu__settings_subtitle.font-bold.text-start.text-lg
              .flex(:class="!smallDevice && '-ms-12'")
                .w-12.flex-none(v-if="!smallDevice")
                  uc-icon.text-3xl.opacity-60(icon="theme:a11y")
                uc-text(text="Accessibility")

            //- SIGN LANGUAGE
            .items-center.menu__settings_option.border-b-2.border-gray-300.flex.justify-end.text-lg(
              class="py-[16px]"
              v-if="hasBsl"
            )
              label.flex.justify-end.items-center.w-full.cursor-pointer
                uc-text.labels.me-auto(text="AI Sign Language")
                input-toggle.controls(@change="toggleBsl" v-model="isBslEnabled") 

            //- FONT SIZE
            .items-center.menu__settings_option.border-b-2.border-gray-300.flex.justify-end.text-lg(
              class="py-[16px]"
            )
              .flex.justify-end.items-center.w-full
                uc-text.labels.me-auto(text="Font size")

              .controls.flex.items-center
                uc-button.btn.btn-icon.ripple.ripple--centered.rounded-full.text-xl.me-2.focus-visible_focus-ring(
                  :disabled="fontSize <= 16"
                  @click="changeFontSize(false)"
                  :aria-label="hasTranslation('Decrease font size')"
                  id="btn_decrease_font_size"
                )
                  uc-text.sr-only(text="Font size minus")
                  uc-icon(icon="theme:minus")

                uc-button.btn.btn-icon.ripple.ripple--centered.rounded-full.text-xl.focus-visible_focus-ring(
                  :disabled="fontSize >= 28"
                  @click="changeFontSize(true)"
                  :aria-label="hasTranslation('Increase font size')"
                  id="btn_increase_font_size"
                )
                  uc-text.sr-only(text="Font size plus")
                  uc-icon(icon="theme:plus")

            //- HIGH CONTRAST
            .items-center.menu__settings_option.border-b-2.border-gray-300.flex.justify-end.text-lg(
              class="py-[16px]"
            )
              label.flex.justify-end.items-center.w-full.cursor-pointer
                uc-text.labels.me-auto(text="High contrast")
                input-toggle.controls(@change="toggleHighContrast" v-model="isHighContrast")            

            //- REDUCE MOTION
            .items-center.menu__settings_option.border-b-2.border-gray-300.flex.justify-end.text-lg(
              class="py-[16px]"
            )
              label.flex.justify-end.items-center.w-full.cursor-pointer
                uc-text.labels.me-auto(text="Reduce motion")
                input-toggle.controls(@change="toggleReducedMotion" v-model="isReducedMotion")            

            //- COURSE TRANSCRIPT DOWNLOAD
            .items-center.menu__settings_option.border-b-2.border-gray-300.flex.justify-end.text-lg(
              class="py-[16px]"
            )
              .flex.justify-end.items-center.w-full(
                :class="fontSize >= 23 && 'flex-wrap'"
              )
                uc-text.labels.me-auto(text="Course transcript")

                .controls.flex.items-center.gap-2
                  button.transcript-button.btn-primary.ripple.ripple--centered.rounded.px-2.py-1.flex.items-center.gap-2(
                    @click="createCourseTranscriptPdf"
                    :disabled="generatingPdf || generatingDoc"
                    title="Download a course transcript document"
                    :aria-label="hasTranslation('Download a course transcript document')"
                    v-if="!isDocTranscript && !isUnsupportedPdfLanguage"
                  )
                    uc-icon.text-2xl.animate-spin(icon="theme:spinner" v-if="generatingPdf")
                    uc-icon.text-2xl(icon="theme:file-pdf" v-else="")
                    uc-text.prose-lg(text="Download")

                  button.transcript-button.btn-primary.ripple.ripple--centered.rounded.px-2.py-1.flex.items-center.gap-2(
                    @click="createCourseTranscriptDocx"
                    :disabled="generatingPdf || generatingDoc"
                    title="Download a course transcript document"
                    :aria-label="hasTranslation('Download a course transcript document')"
                    v-else=""
                  )
                    uc-icon.text-2xl.animate-spin(icon="theme:spinner" v-if="generatingDoc")
                    uc-icon.text-2xl(icon="theme:file-word" v-else="")
                    uc-text.prose-lg(text="Download")

        //- Course structure view
        .menu__main.overflow-hidden.flex.flex-col.flex-1(v-else="")
          //- instructions
          .menu__instructions.p-4.ps-12.py-2.flex.items-center.text-gray-700.text-sm.prose(
            v-if="pages.length > 1 && !isSubunitOpen"
          )
            uc-text.italic(
              text="You have completed the course."
              type="p"
              v-if="suspend.passed"
            )

            //- Translations cannot handle custom unit labels!
            //- text="You have completed ${completedCount} of ${unitCount} units."
            uc-text.italic(
              :text="'You have completed ${completedCount} of ${unitCount} ${unitLabel}s.'"
              :values="{ unitLabel: unitLabel.toLowerCase() }"
              k="You have completed ${completedCount} of ${unitCount} units."
              type="p"
              v-else-if="completedCount > 0"
            )

            //- Translations cannot handle custom unit labels!
            //- text="Complete all ${unitCount} units to complete the course."
            uc-text.italic(
              :text="'Complete all ${unitCount} ${unitLabel}s to complete the course.'"
              :values="{ unitLabel: unitLabel.toLowerCase() }"
              k="Complete all ${unitCount} units to complete the course."
              type="p"
              v-else=""
            )

          //- pages
          .nav_menu_pages.menu__scroller.flex-1.overflow-y-auto.h-full.overflow-x-hidden.simple-scrollbar
            .nav_menu_page(
              :class="[{ 'hidden': !isEditModeActive && isHidden(page.id) }]"
              :key="page.id"

              v-for="(page, index) in isSubunitOpen ? currentSubunit : filterTags(pages)" 
            )
              .p-4.py-2.flex.items-center.relative(
                :class="[{ 'text-primary-700 bg-primary-300/50': currentLocation === page.id }]"
                v-if="pages.length > 1"
              )
                .fill.saturate-1.opacity-20.hatching(
                  v-if="isEditModeActive && isHidden(page.id)"
                )
                //- expose these strings for the translate script
                //- text="${i} of ${n}"
                //- text="Completed"
                button.z-10.text-start.inline-flex.items-center.font-bold.disabled_pointer-events-none.group(
                  :class="{ 'opacity-30': locked(page) }"
                  :disabled="locked(page) || isHidden(page.id)"
                  @click="go(page.id)"
                  data-sfx="menu-link"
                  :aria-label="`${hasTranslation(`${page.id}.title`, page.title)} ${ariaCountLabel(incrementIndex(index), filterTags(pages).length)} ${completed(page) ? hasTranslation('Completed') : ''}`"
                  :aria-current="ariaCurrentPage(page.id)"
                )
                  span.w-6.me-2.inline-flex.items-center.text-center
                    uc-icon(icon="theme:lock" v-if="locked(page)")

                    uc-icon.text-xl.text-primary-500.dark_text-white(
                      icon="theme:check-circle"
                      v-else-if="completed(page)"
                    )

                    span.text-xs.text-primary-500.dark_text-white.tracking-tighter.w-full(
                      v-else=""
                    ) {{ pageProgress(page) }}

                  //- TODO use "name"
                  uc-text.me-10.group-focus-visible_ring.fancy-hover.hover_text-primary-800.prose.focus-visible_focus-ring(
                    :class="{ 'opacity-40': isHidden(page.id) }"
                    :k="`${page.id}.title`"
                    :text="removeLinebreaks(page.title)"
                  )

                button.z-0.fill.w-full.group.flex.flex-0.items-center.justify-center.cursor-default.transition.focus-visible_focus-ring(
                  :disabled="isHidden(page.id)"
                  :aria-label="hasTranslation(openUnit === page.id ? 'Collapse' : 'Expand')"
                  @click="openUnit = openUnit === page.id ? null : page.id"
                  class="hover_bg-black/[0.03]"
                  data-sfx="menu-link"
                  v-if="screens(page).length > 0"
                )
                  .ms-auto.me-2.h-7.w-7.opacity-50.hover_opacity-70.transition-all.rotate-0
                    uc-icon.dark_text-white.rotate-0.transition-all(
                      :class="{ 'rotate-180': openUnit === page.id }"
                      icon="theme:down"
                    )
                    .fill.bg-transparent.transition-all.border-8.border-transparent.rounded-full.-z-1.group-focus-visible_ring(
                      class="group-hover_bg-black/10"
                    )

              //- screens
              transition-expand(:scroll="true")
                .nav_menu_screens.py-2(
                  :class="{ 'bg-gray-300/20': pages.length > 1 }"
                  v-if="openUnit === page.id || pages.length === 1"
                )                  
                  .nav_menu_screen.p-4.ps-12.py-2.relative(
                    :class="[{ 'opacity-30': isScreenLocked(screen) }, { 'hidden': !isEditModeActive && isHidden(screen.id) }]"
                    :disabled="isScreenLocked(screen)"
                    :key="screen.id"
                    v-for="(screen, index) in filterTags(screens(page))"
                  )
                    .fill.saturate-1.opacity-20.hatching(
                      v-if="isEditModeActive && isHidden(screen.id)"
                    )                 
                    .text-start.disabled_opacity-30.flex.items-center.transition-colors 
                      uc-icon.me-3(
                        icon="theme:lock"
                        v-if="isScreenLocked(screen)"
                      )
                      uc-icon.me-3.text-primary-500(
                        icon="theme:check"
                        v-else-if="!unlockScreens"
                      )
                      button.inline.fancy-hover.text-start.text-black.hover_text-primary-800.disabled_text-black.prose.focus-visible_focus-ring(
                        :data-screen="screen.id"
                        :disabled="isScreenLocked(screen)"
                        @click="goToScreen(page.id, screen.id)"
                        data-sfx="menu-link"
                        :aria-label="`${hasTranslation(`${screen.id}.bookmark`, screen.bookmark)} ${ariaCountLabel(incrementIndex(index), filterTags(screens(page)).length)} ${completed(screen) ? hasTranslation('Completed') : ''}`"
                      )
                        uc-text(
                          :k="`${screen.id}.bookmark`"
                          :text="screen.bookmark"
                        )
        .text-center.justify-start(v-if="!isSettings && isSubunitOpen")
          p.mt-8.mb-6.flex.gap-6.justify-center.align-center
            uc-button.btn.btn-primary(@click="goToScreen(parentDashboardUnitId, parentDashboardScreenId)")
              uc-text(
                text="Return to Dashboard"
                type="span"
              )

        .menu__footer.p-2.bg-gray-100.flex.justify-end
          .menu__terms.pb-4.pe-4.flex.items-start.text-sm.text-gray-500
            a.focus-visible_focus-ring(
              href="https://vinciworks.com/privacy-notice/"
              target="_blank"
            )
              uc-text(
                text="Privacy Notice"
                type="span"
              )
          uc-logo.h-auto.max-w-full.object-contain(
            v-if="!isEditModeActive && (content.course.logo === true || content.course.logo === undefined)"
            class="!mx-0 w-[3em]"
            :showLogoIcon="true"
            )

        iframe.absolute.pointer-events-none.hidden(
          ref="courseTranscriptIFrame"
          referrerpolicy="origin-when-cross-origin"
          style="width: 100%; height: 90%; border: 0;"
          v-if="isOpen && isSettings"
        )
</template>

<script lang="ts">
import gsap from 'gsap';
import SCORM, {
  get,
  set,
  setTags,
  getFirm,
  getCourseId,
  isFirm
} from '../lib/SCORM';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { nextTick, ref, inject } from 'vue';
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';
import { throttle, debounce } from 'lodash-es';
import useEditMode from '../stores/useEditMode';
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';
import {
  mute,
  unmute,
  setVolume,
  getVolume,
  setPlaybackRate,
  getPlaybackRate
} from '../lib/audio';
import { disableSfx, enableSfx } from '../lib/sfx';
import useBadges from '../stores/useBadges';
import useTranslations from '../stores/useTranslations';
import isHidden from '../lib/isHidden';
import useAudio from '../stores/useAudio';
import removeMarkdown from '../../esm/lib/removeMarkdown.js';
import languageData from '../lib/languages.json';
import removeLinebreaks from '../lib/removeLinebreaks';
import useAudioNarration from '../lib/useAudioNarration';

const debouncedSet = debounce(set, 500);

function computeScrollProgress(scroller: HTMLDivElement) {
  if (window.__hmr_updating) return;
  if (!scroller) return;

  const sections = scroller.querySelectorAll<HTMLDivElement>(
    '.scroller > div > .section:not([data-section-hidden="true"])'
  );

  let progressedSections = 0;
  const sh = scroller.offsetHeight;

  sections.forEach(s => {
    const { top, bottom, height } = s.getBoundingClientRect();
    if (height === 0) return;
    if (top <= sh && bottom <= sh) progressedSections += 1;
    if (top <= sh && bottom > sh) progressedSections += (sh - top) / height;
  });

  const round = (n: number) => Math.ceil(n * 100) / 100;
  const fraction = round(progressedSections / sections.length);

  return fraction;
}

export default {
  props: {
    bslEnabled: Boolean
  },
  inject: [
    'suspend',
    'content',
    'unitsAndTest',
    'completedCount',
    'finalUnlocked',
    'unlockScreens',
    'unlockUnits',
    'currentLocation',
    'goToSection',
    'isEditModeAvailable',
    'isEditModeActive',
    'thumbnailType'
  ],

  setup() {
    const hasAudio = inject('hasAudio');
    const hasSpeech = inject('hasSpeech');
    const hasCaptions = inject('hasCaptions');
    const hasSfx = inject('hasSfx');
    const hasVideo = inject('hasVideo');
    const hasVoicemail = inject('hasVoicemail');
    const hasBsl = inject('hasBsl');
    const elRef = ref();
    const { activate, deactivate } = useFocusTrap(elRef);
    const activateFocusTrap = activate;
    const deactivateFocusTrap = deactivate;
    const { available: editModeAvailable } = useEditMode();
    const { getPageBadge, getBadgeImage } = useBadges();
    const { translations } = useTranslations();
    const { getAudioURL } = useAudio();
    const breakpoints = useBreakpoints(breakpointsTailwind);
    const smallDevice = breakpoints.smallerOrEqual('sm');
    const courseTranscriptIFrame = ref<HTMLIFrameElement>();
    const generatingDoc = ref(false);
    const generatingPdf = ref(false);
    const { audioAiJitEnabled } = useAudioNarration();

    return {
      activateFocusTrap,
      deactivateFocusTrap,
      editModeAvailable,
      elRef,
      getAudioURL,
      getPageBadge,
      getBadgeImage,
      hasAudio,
      hasSpeech,
      hasCaptions,
      hasSfx,
      hasVideo,
      hasVoicemail,
      hasBsl,
      translations,
      smallDevice,
      courseTranscriptIFrame,
      generatingDoc,
      generatingPdf,
      audioAiJitEnabled
    };
  },

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data(): { scroller: HTMLDivElement; [key: string]: any } {
    return {
      isOpen: false,
      scroller: null,
      scrollHeight: 1,
      scrollProgress: 0,
      st: null,
      openUnit: null,
      completedLessons: {},
      unlockedLessons: {},
      settingsOpened: false,
      isSettings: false,
      isSfxEnabled: false,
      isAudioEnabled: false,
      isVideoAudioEnabled: false,
      isCaptionsEnabled: false,
      isTranscriptEnabled: false,
      isHighContrast: false,
      isReducedMotion: false,
      isBslEnabled: true,
      fontSize: 16,
      pdfUrl: null,
      audioVolume: 1,
      audioPlaybackRate: 1
    };
  },

  computed: {
    isKitchenSink() {
      return this.content?.course?.title?.trim() === 'Ubercourse';
    },
    progress() {
      const value = this.scrollProgress;
      const size = 48;
      const stroke = 4;
      const center = size / 2;
      const radius = size / 2 - stroke / 2;
      const circumference = radius * 2 * Math.PI;
      const offset = (1 - value) * circumference;

      return { value, center, radius, circumference, offset, size, stroke };
    },
    allUnits() {
      return this.content.pages;
    },
    allSubunits() {
      return this.allUnits.filter(unit => unit.type === 'subunit');
    },
    isSubunitOpen() {
      return this.allSubunits.some(subunit => subunit.id === this.openUnit);
    },
    currentSubunit() {
      return this.allSubunits.filter(subunit => subunit.id === this.openUnit);
    },
    includedUnits() {
      // FIXME use tags
      return this.units;
    },
    pages() {
      return (
        this.content?.pages?.filter(
          p => ['unit', 'final'].includes(p.type) && !p.skipMenu
        ) || []
      );
    },
    unitLabel() {
      return this.content.course['unit-label'] || 'Unit';
    },
    pageBadge() {
      return this.getPageBadge(this.currentLocation);
    },
    pageBadgeImage() {
      return this.getBadgeImage(this.pageBadge);
    },
    allMuted() {
      const audioMuted =
        this.hasAudio || this.hasSpeech || this.hasVoicemail
          ? this.suspend.mute
          : true;
      const videoMuted = this.hasVideo ? this.suspend.videoMute : true;
      const sfxMuted = this.hasSfx ? this.suspend.disableSfx : true;
      return audioMuted && videoMuted && sfxMuted;
    },
    parentDashboardScreenId() {
      return this.content.pages
        .map(page => page.screens)
        .flat()
        .filter(screen => screen.template === 'subunit-dashboard')
        .filter(screen => screen.subunits?.includes(this.openUnit))[0]
        ?.id.toString();
    },
    parentDashboardUnitId() {
      return this.content.pages
        .filter(page =>
          page.screens
            .map(screen => screen.id)
            .includes(this.parentDashboardScreenId)
        )[0]
        ?.id.toString();
    },
    courseTranscriptUrl() {
      const courseId = getCourseId();
      const firmCode = getFirm();
      const clientEditMode = get('clientEditMode');
      const localServer = window.location.origin.includes('local');
      const portalOrigin = new URL(window.location.href).origin;
      const pathName = new URL(window.location.href).pathname;
      const courseQ = `courseid=${courseId}`;
      const firmQ = firmCode ? `firm=${firmCode}` : '';
      const langQ = `lang=${this.suspend.locale || 'en-GB'}`;
      const tagQ = 'tags=worddoc+includeall&word=1';
      const editModeQ = clientEditMode ? 'sd=clientEditMode=1;' : '';
      const queryString = [
        'turbo=999999',
        courseQ,
        firmQ,
        langQ,
        tagQ,
        editModeQ
      ]
        .filter(Boolean)
        .join('&');

      return localServer
        ? `./app.html?${queryString}`
        : `${portalOrigin}${pathName}?${queryString}`;
    },
    isDocTranscript() {
      return this.content.course['doc-transcript'] === true;
    },
    isUnsupportedPdfLanguage() {
      // any languages without font support in the PDF library (specified in languages.json)
      return languageData?.[this.suspend.locale]?.transcriptFormat === 'docx';
    }
  },

  watch: {
    isOpen(val) {
      this.openToUnit();

      nextTick(() => {
        if (val) {
          // FIXME not defined in production
          this?.activateFocusTrap();
        } else {
          this?.deactivateFocusTrap();
          // focus on settings button after closing settings
          if (!this.isSettings && this.settingsOpened) {
            this.$el.querySelector('.settings-button')?.focus();
            this.settingsOpened = false;
          }
        }
      });
    },
    openUnit(id) {
      try {
        this.scrollToUnit(id);
      } catch (e) {
        console.warn(e);
      }
    },
    'progress.value': function (val) {
      if (!this.currentLocation) return;

      const progressKey = `progress_${this.currentLocation}`;
      const storedValue = this.suspend[progressKey] || 0;

      // Only update if the new value is greater than the stored value
      if (val > storedValue) {
        debouncedSet(progressKey, val);
      }
    },
    allMuted: function (val) {
      SCORM.set('mute', val);
    },
    'suspend.disableSfx': function (val) {
      this.isSfxEnabled = !val;
    },
    'suspend.reducedMotion': function (val) {
      this.isReducedMotion = val;
    },
    'suspend.narrationMute': function (val) {
      this.isAudioEnabled = !val;
    },
    'suspend.videoMute': function (val) {
      this.isVideoAudioEnabled = !val;
    },
    'suspend.captions': function (val) {
      this.isCaptionsEnabled = val;
    },
    'suspend.transcript': function (val) {
      this.isTranscriptEnabled = val;
    }
    // async currentLocation(next, prev) {
    //   if (next && next !== prev) {
    //     await nextTick();
    //     await nextTick();
    //     await nextTick();
    // //     console.log('INIT PAGE WATCH');
    //     this.initPage();
    //   }
    // }
  },

  mounted() {
    window.addEventListener('pageenter', async e => {
      // update page progress

      await nextTick();
      await nextTick();
      await nextTick();

      // console.log('INIT PAGE EVENT');
      this.initPage();
    });

    // console.log('INIT PAGE MOUNT');
    this.initPage();

    ScrollTrigger.addEventListener('refresh', throttle(this.updateScroll, 100));

    // update from suspend data once mounted
    this.isAudioEnabled = !this.suspend.narrationMute;
    this.isSfxEnabled = !this.suspend.disableSfx;
    this.isVideoAudioEnabled = !this.suspend.videoMute;
    this.isCaptionsEnabled = this.suspend.captions;
    this.isTranscriptEnabled = this.suspend.transcript;
    this.isReducedMotion = this.suspend.reducedMotion;
    this.audioVolume = getVolume();
    this.audioPlaybackRate = getPlaybackRate();
  },

  methods: {
    isHidden,
    removeMarkdown,
    removeLinebreaks,
    toggleBsl() {
      this.$emit('update:bslEnabled', !this.bslEnabled);
    },
    initPage() {
      this.scroller = document.querySelector(
        `#${this.currentLocation} .scroll-page__content`
      );

      if (this.scroller) {
        const throttledUpdateScroll = throttle(this.updateScroll, 100);

        setTimeout(() => {
          throttledUpdateScroll();
          this.openToUnit();
          this.scroller.addEventListener('scroll', throttledUpdateScroll);
        }, 100);
      }
    },

    updateLessons() {
      //   Object.keys(this.menu).forEach(id => {
      //     this.menu[id].forEach(lesson => {
      //       this.completedLessons[lesson.tag] = lesson.sections.every(
      //         s => this.$root.suspend.completed[s]
      //       );
      //       this.unlockedLessons[lesson.tag] = this.completedLessons[
      //         lesson.target
      //       ] = lesson.sections.some(s => this.$root.suspend.viewed[s]);
      //     });
      //   });
      // //   console.log(this.completedLessons, this.unlockedLessons);
    },

    async openToUnit() {
      // open the unit
      const id = this.currentLocation;
      this.openUnit = id;
    },

    scrollToUnit(id) {
      const unitEl = this.$el.querySelector(`[data-unit="${id}"]`);

      if (unitEl) {
        const offsetY = Math.max(
          0,
          window.innerHeight / 2 - unitEl.offsetHeight / 2 - 18
        );
        gsap.to(this.$refs.units, { scrollTo: { y: unitEl, offsetY } });
      }
    },

    updateScroll() {
      this.scrollProgress = computeScrollProgress(this.scroller);
    },

    toggleOpen() {
      if (this.isOpen) {
        this.isSettings = false;
      }

      this.isOpen = !this.isOpen;

      // reset doc download state
      this.generatingPdf = false;
      this.generatingDoc = false;
    },

    toggleBadges() {
      document.dispatchEvent(new CustomEvent('toggleBadges'));
    },

    toggleHighContrast() {
      const element = document.getElementsByTagName('html')[0];

      if (this.isHighContrast) {
        element.classList.add('high-contrast');
      } else {
        element.classList.remove('high-contrast');
      }
    },

    toggleReducedMotion() {
      const element = document.getElementsByTagName('html')[0];

      if (this.isReducedMotion) {
        element.classList.add('screen--no-fade');
      } else {
        element.classList.remove('screen--no-fade');
      }

      set('reducedMotion', this.isReducedMotion);

      ScrollTrigger.refresh();
    },

    toggleAudio() {
      this.isAudioEnabled ? unmute() : mute();
    },

    toggleSfx() {
      this.isSfxEnabled ? enableSfx() : disableSfx();
    },

    toggleVideoAudio() {
      set('videoMute', !this.isVideoAudioEnabled);
    },

    toggleVideoCaptions() {
      set('captions', this.isCaptionsEnabled);
    },

    toggleVideoTranscript() {
      set('transcript', this.isTranscriptEnabled);
    },

    changeFontSize(isIncrease) {
      let txt = document.documentElement;
      let style = window
        .getComputedStyle(txt, null)
        .getPropertyValue('font-size');
      let currentSize = parseFloat(style);
      if (isIncrease) {
        let newFontSize = currentSize + 2;
        txt.style.fontSize = newFontSize + 'px';
        this.fontSize = newFontSize;
      } else {
        let newFontSize = currentSize - 2;
        txt.style.fontSize = newFontSize + 'px';
        this.fontSize = newFontSize;
      }

      if (parseFloat(txt.style.fontSize) >= 28) {
        txt.style.fontSize = '28px';
        document.getElementById('btn_decrease_font_size').focus();
      } else if (parseFloat(txt.style.fontSize) <= 16) {
        txt.style.fontSize = '16px';
        document.getElementById('btn_increase_font_size').focus();
      }
    },

    changeVolume() {
      setVolume(+this.audioVolume);
    },

    changePlaybackRate(isIncrease) {
      if (isIncrease) {
        this.audioPlaybackRate += 0.25;
      } else {
        this.audioPlaybackRate -= 0.25;
      }

      setPlaybackRate(+this.audioPlaybackRate);
    },

    async go(page) {
      this.isOpen = false;
      setTimeout(() => {
        this.$router.push('/' + page);
      }, 50);
    },

    async goToScreen(pageId, screenId) {
      this.isOpen = false;

      await this.goToSection(pageId, screenId);
    },

    onToggleChange(e) {
      setTags({ [e.target.name]: e.target.checked });
    },

    screens(page) {
      const str = page.screens.filter(s => s.bookmark);

      return str;
    },

    isScreenLocked(screen) {
      return this.suspend.viewed[screen.id] || this.suspend.completed[screen.id]
        ? false
        : !this.unlockScreens;
    },

    ariaCurrentPage(id) {
      return this.currentLocation === id ? 'page' : 'false';
    },

    completed(page) {
      return this.suspend.completed[page?.id];
    },

    pageProgress(page) {
      const fr = this.suspend[`progress_${page.id}`] || 0;
      const pct = Math.min(Math.round(fr * 100), 100);
      return pct > 0 ? pct + '%' : '';
    },

    locked(page) {
      const { suspend, content, unitsAndTest: units } = this;
      const { linear } = content.course;
      const { completed } = suspend;

      if (this.unlockUnits) {
        return false;
      }

      if (page.type === 'final') {
        return !this.finalUnlocked;
      }

      // unlock all units when the course isn't linear
      if (!linear) {
        return false;
      }

      let unlocked = true;
      let i = units.findIndex(u => u.id === page.id);
      while (i >= 0 && i-- && unlocked) {
        const prevId = units[i].id;

        if (!completed[prevId]) {
          unlocked = false;
        }
      }

      return !unlocked;
    },

    createCourseTranscriptPdf() {
      this.createCourseTranscript('pdf');
    },

    createCourseTranscriptDocx() {
      this.createCourseTranscript('docx');
    },

    async createCourseTranscript(fileType) {
      fileType === 'pdf'
        ? (this.generatingPdf = true)
        : (this.generatingDoc = true);

      const firm = getFirm();
      const courseId = getCourseId();
      const locale = this.suspend.locale;

      // skip firms like "___" and REVIEW.
      const firmTerm =
        /^[a-zA-Z]/.test(firm) && !isFirm('REVIEW') ? firm.toUpperCase() : null;

      const iframe = this.courseTranscriptIFrame;
      if (!iframe) {
        throw new Error('Transcript iframe not found - please try again');
      }

      // Set up load promise before changing src
      const loadPromise = new Promise((resolve, reject) => {
        const handleLoad = () => {
          console.log('Iframe loaded successfully');
          iframe.removeEventListener('load', handleLoad);
          resolve(true);
        };

        const handleError = (error: ErrorEvent) => {
          console.error('Iframe load error:', error);
          iframe.removeEventListener('error', handleError);
          reject(new Error('Failed to load iframe content'));
        };

        iframe.addEventListener('load', handleLoad);
        iframe.addEventListener('error', handleError);
      });

      // Set iframe source
      iframe.src = this.courseTranscriptUrl;
      console.log('Loading transcript iframe with URL:', iframe.src);

      // Wait for load
      await loadPromise;

      // Wait for pages element to be available
      const pages = await new Promise<Element>((resolve, reject) => {
        const checkForPages = () => {
          const cw = iframe.contentWindow as Window;
          const pages = cw.document.documentElement.querySelector('.pages');
          if (pages) {
            resolve(pages);
          } else {
            setTimeout(checkForPages, 500);
          }
        };
        checkForPages();
      });

      // combine course title and subtitle for document title
      const courseTitle = this.content.course.title
        ? this.removeMarkdown(
            this.removeLinebreaks(this.content.course.title)
          ).trim()
        : 'Course';

      const subTitle = this.content.course.subtitle
        ? this.removeMarkdown(
            this.removeLinebreaks(this.content.course.subtitle)
          ).trim()
        : undefined;

      const docTitle = `${courseTitle}${subTitle ? `: ${subTitle}` : ''}`;
      const fileTitle = `${courseTitle}${subTitle ? ` - ${subTitle}` : ''}`;

      let fileName = `${fileTitle} [Transcript ${
        firmTerm ? firm + '_' : ''
      }${courseId} ${locale}]`;

      // replace any invalid characters in the filename with a space
      // eslint-disable-next-line no-control-regex -- intentionally strips control chars from filenames
      fileName = fileName.replace(/[<>:"/\\|?*\x00-\x1F]/g, ' ');
      // replace multiple underscores with a single one
      fileName = fileName.replace(/__+/g, '_');
      // replace multiple spaces with a single one
      fileName = fileName.replace(/\s+/g, ' ');

      // Generate word/pdf document
      try {
        const { default: createTranscript } =
          await import('../lib/createTranscript');
        await createTranscript(pages, locale, docTitle, fileName, fileType);
      } catch (e) {
        console.error('Failed to generate transcript:', e.message);
      }

      fileType === 'pdf'
        ? (this.generatingPdf = false)
        : (this.generatingDoc = false);
    },

    incrementIndex(index: number) {
      return index + 1;
    },

    hasTranslation(key: string | number, fallback: string | number = key) {
      // get the translation if it exists
      let maybeTranslated =
        this.translations?.[this.suspend.locale]?.[key] ||
        this.translations?.[this.suspend.locale]?.[fallback];

      return maybeTranslated || fallback;
    },

    ariaCountLabel(i: number, n: number) {
      const translation = this.hasTranslation('${i} of ${n}');
      return translation.replace('${i}', i).replace('${n}', n);
    }
  }
};
</script>

<style scoped>
.uc-nav-menu {
  .menu__progress-fill {
    transition: stroke-dashoffset 0.5s ease-out;
  }

  .menu__scroller {
    /* scrolling shadow with https://lea.verou.me/2012/04/background-attachment-local/ */

    --bg: var(--color-gray-100);
    --sh: var(--color-gray-500);

    background:
      linear-gradient(rgb(var(--bg)) 30%, rgb(var(--bg) / 0%)),
      linear-gradient(rgb(var(--bg) / 0%), rgb(var(--bg)) 70%) 0 100%,
      radial-gradient(
        farthest-side at 50% 0,
        rgb(var(--sh) / 20%),
        rgb(var(--sh) / 0%)
      ),
      radial-gradient(
          farthest-side at 50% 100%,
          rgb(var(--sh) / 20%),
          rgb(var(--sh) / 0%)
        )
        0 100%;
    background:
      linear-gradient(rgb(var(--bg)) 30%, rgb(var(--bg) / 0%)),
      linear-gradient(rgb(var(--bg) / 0%), rgb(var(--bg)) 70%) 0 100%,
      /* Shadows */
      radial-gradient(
          farthest-side at 50% 0,
          rgb(var(--sh) / 20%),
          rgb(var(--sh) / 0%)
        ),
      radial-gradient(
          farthest-side at 50% 100%,
          rgb(var(--sh) / 20%),
          rgb(var(--sh) / 0%)
        )
        0 100%;
    background-repeat: no-repeat;
    background-color: rgb(var(--bg));
    background-size:
      100% 60px,
      100% 60px,
      120% 21px,
      120% 21px;
    background-position:
      center 0%,
      center 100%,
      center 0%,
      center 100%;

    /* Opera doesn't support this in the shorthand */
    background-attachment: local, local, scroll, scroll;
  }

  .slider {
    -webkit-appearance: none;
    display: block;
    width: 100%;
    height: 8px;
    margin: 10px 0;
    background: rgb(var(--color-gray-300));
    border-radius: 20px;

    &::-webkit-slider-runnable-track {
      width: 100%;
      height: 8px;
      cursor: pointer;
      box-shadow: none;
      background: transparent;
      border-radius: 0px;
      border: none;
    }

    &:disabled::-webkit-slider-runnable-track {
      cursor: default;
    }

    &:focus::-webkit-slider-runnable-track {
      background: transparent;
    }

    &::-webkit-slider-thumb {
      box-shadow: none;
      border: 4px solid rgb(var(--color-blue-600));
      height: 14px;
      width: 14px;
      border-radius: 50%;
      background: rgb(var(--color-blue-600));
      cursor: grab;
      -webkit-appearance: none;
      margin-top: -3px;
    }

    &:active::-webkit-slider-thumb {
      cursor: grabbing;
    }

    &:disabled::-webkit-slider-thumb {
      border: 4px solid rgb(var(--color-gray-400));
      background: rgb(var(--color-gray-400));
      cursor: default;
    }

    &::-moz-range-thumb {
      box-shadow: none;
      border: 4px solid rgb(var(--color-blue-600));
      height: 10px;
      width: 10px;
      border-radius: 50%;
      background: rgb(var(--color-blue-600));
      cursor: grab;
    }

    &:active::-moz-range-thumb {
      cursor: grabbing;
    }

    &:disabled::-moz-range-thumb {
      border: 4px solid rgb(var(--color-gray-400));
      background: rgb(var(--color-gray-400));
      cursor: default;
    }

    &::-moz-range-track {
      width: 100%;
      height: 4px;
      cursor: pointer;
      box-shadow: none;
      background: transparent;
      border-radius: 0px;
      border: none;
    }

    &:disabled::-moz-range-track {
      cursor: auto;
    }
  }

  /* course transcript download button (overrides any theme/custom specifics) */
  .transcript-button {
    @apply bg-blue-600 !important;
    @apply text-white !important;

    &:hover {
      @apply bg-blue-900 !important;
    }
  }

  /* hide the captions settings on Safari (mobile and desktop) */
  .items-center.menu__settings_option.video-captions {
    @supports (-webkit-backdrop-filter: none) {
      display: none;
    }
  }
}

/* align the menu with the badge nav, but only on wide screens */
@media (min-width: 1440px) {
  .nav-menu-badge-ml {
    margin-left: 2rem;
  }
}
</style>
